import React, { useMemo } from "react";

const splitActivityDetails = (value) => {
  if (typeof value !== "string") {
    return { activity: "", details: "" };
  }
  const delimiterIndex = value.indexOf("||");
  if (delimiterIndex === -1) {
    return { activity: value, details: "" };
  }
  return {
    activity: value.slice(0, delimiterIndex),
    details: value.slice(delimiterIndex + 2),
  };
};

const normalizeText = (value) => {
  if (typeof value !== "string") return "";
  return value.replace(/\\n/g, "\n").trim();
};

export const formatActivityDetails = (details) => {
  if (!details) return details;

  const text = typeof details === "string" ? details : String(details);

  const looksLikeCode =
    /\bdef\s+\w+\s*\(/.test(text) ||
    /\breturn\b/.test(text) ||
    /\bfor\s+\w+\s+in\b/.test(text) ||
    /\bprint\s*\(/.test(text) ||
    /\{[^}]*\w+\[[^\]]+\][^}]*\}/.test(text);
  if (looksLikeCode) {
    return text.replace(/\\n/g, "\n");
  }

  let formatted = text;

  const tryParseJSON = (content) => {
    const pythonishToJson = (value) => {
      return value
        .replace(/\bNone\b/g, "null")
        .replace(/\bTrue\b/g, "true")
        .replace(/\bFalse\b/g, "false")
        .replace(/'([^'\\]*?)'(?=\s*:)/g, '"$1"')
        .replace(/'([^'\\]*?)'/g, '"$1"');
    };

    try {
      const trimmed = content.trim();
      if (!(trimmed.startsWith("{") || trimmed.startsWith("["))) {
        throw new Error("not-json-start");
      }
      const jsonData = JSON.parse(trimmed);
      return JSON.stringify(jsonData, null, 2);
    } catch {
      try {
        let cleaned = content.trim();
        if (
          (/^[\[{]/.test(cleaned) && /[\]}]$/.test(cleaned)) ||
          cleaned.includes("':") ||
          cleaned.includes("'")
        ) {
          const normalized = pythonishToJson(cleaned);
          try {
            const jsonData = JSON.parse(normalized);
            return JSON.stringify(jsonData, null, 2);
          } catch {}
        }
      } catch {}
    }
    return null;
  };

  const jsonResult = tryParseJSON(text);
  if (jsonResult) {
    return jsonResult;
  }

  const detectAndFormatList = (content) => {
    const pythonListMatch = content.match(/^\[.*\]$/);
    if (pythonListMatch) {
      try {
        const jsonCompatible = content.replace(/'/g, '"');
        const parsed = JSON.parse(jsonCompatible);
        if (Array.isArray(parsed)) {
          return parsed.map((item, index) => `${index + 1}. ${item}`).join("\n");
        }
      } catch {}

      const items = content.match(/'([^']*)'/g);
      if (items && items.length > 0) {
        return items
          .map((item, index) => {
            const cleanItem = item.replace(/'/g, "").trim();
            return `${index + 1}. ${cleanItem}`;
          })
          .join("\n");
      }
    }
    return null;
  };

  const listResult = detectAndFormatList(text);
  if (listResult) {
    return listResult;
  }

  const isListContent = (content) => {
    const lines = content.split(/[\n|]+/).filter((line) => line.trim());

    const hasBullets = lines.some(
      (line) =>
        line.trim().match(/^[-*•]\s/) ||
        line.trim().match(/^\d+\.\s/) ||
        line.trim().match(/^[a-z]\)\s/) ||
        line.trim().match(/^[A-Z]\.\s/)
    );

    const hasConsistentPattern =
      lines.length > 1 &&
      lines.every(
        (line) =>
          line.trim().length > 0 &&
          (line.trim().startsWith("-") ||
            line.trim().startsWith("*") ||
            line.trim().startsWith("•") ||
            line.trim().match(/^\d+\./) ||
            line.trim().match(/^[a-z]\)/) ||
            line.trim().match(/^[A-Z]\./))
      );

    const hasRepeatedPatterns =
      lines.length > 2 &&
      lines.filter((line) => line.trim().length > 0).length > 2 &&
      (lines.some((line) => line.includes(":")) ||
        lines.some((line) => line.includes("=")) ||
        lines.some((line) => line.includes("→")) ||
        lines.some((line) => line.includes("→")));

    return hasBullets || hasConsistentPattern || hasRepeatedPatterns;
  };

  if (text.includes("\n")) {
    const lines = text.split(/\n+/).filter((line) => line.trim());

    if (isListContent(text)) {
      formatted = lines
        .map((line) => {
          const trimmed = line.trim();
          if (trimmed.match(/^[-*•]\s/)) {
            return `• ${trimmed.substring(1).trim()}`;
          }
          if (trimmed.match(/^\d+\.\s/)) {
            return trimmed;
          }
          if (trimmed.match(/^[a-z]\)\s/)) {
            return trimmed;
          }
          if (trimmed.match(/^[A-Z]\.\s/)) {
            return trimmed;
          }
          if (trimmed.includes(":") && !trimmed.includes("http")) {
            const [key, ...valueParts] = trimmed.split(":");
            const value = valueParts.join(":").trim();
            return `  ${key.trim()}: ${value}`;
          }
          return `• ${trimmed}`;
        })
        .join("\n");
    } else {
      formatted = lines.join("\n");
    }
  }

  if (!isListContent(text) && (text.includes(":") || text.includes("="))) {
    const lines = text.split(/\n+/).filter((line) => line.trim());
    formatted = lines
      .map((line) => {
        const trimmed = line.trim();
        if (trimmed.includes(":") && !trimmed.includes("http")) {
          const [key, ...valueParts] = trimmed.split(":");
          const value = valueParts.join(":").trim();
          return `${key.trim()}: ${value}`;
        }
        return trimmed;
      })
      .join("\n");
  }

  if (text.includes("<") && text.includes(">")) {
    formatted = text
      .replace(/>/g, ">\n")
      .replace(/</g, "\n<")
      .split("\n")
      .filter((line) => line.trim())
      .join("\n");
  }

  if (text.includes("http") || text.includes("/")) {
    const lines = text.split(/[\n|]+/);
    formatted = lines
      .map((line) => {
        const trimmed = line.trim();
        if (trimmed.length > 80 && (trimmed.includes("http") || trimmed.includes("/"))) {
          return trimmed.replace(/(https?:\/\/[^\/]+)/, "$1\n  ");
        }
        return trimmed;
      })
      .join("\n");
  }

  return formatted;
};

export const isJsonContent = (content) => {
  if (!content) return false;
  try {
    if (typeof content === "string") {
      JSON.parse(content);
      return true;
    }
    if (typeof content === "object") return true;
    return false;
  } catch (e) {
    return false;
  }
};

export const parseActivityMessage = (message, options = {}) => {
  const { formatDetails = true } = options;
  if (!message || typeof message !== "string") {
    return { activity: "Unknown activity", details: null, hasDetails: false };
  }

  let activityText = "";
  let detailsText = "";

  try {
    const parsed = JSON.parse(message);
    if (parsed && typeof parsed === "object") {
      activityText =
        parsed.activity || parsed.current_activity || parsed.message || "Activity update";
      detailsText = parsed.details || parsed.output || parsed.log || parsed.raw || "";
    }
  } catch {
    // fall through to plain text handling
  }

  const baseText = activityText || message;
  const split = splitActivityDetails(baseText);
  const combinedDetails = [detailsText, split.details].filter(Boolean).join("\n\n");
  const normalizedActivity = normalizeText(split.activity) || "Activity";
  const normalizedDetails = normalizeText(combinedDetails);
  const formattedDetails = formatDetails
    ? formatActivityDetails(normalizedDetails)
    : normalizedDetails;

  return {
    activity: normalizedActivity,
    details: formattedDetails || null,
    hasDetails: Boolean(formattedDetails),
  };
};

const getPreviewText = (details, limit) => {
  if (!details) return "";
  let text = "";
  if (typeof details === "string") {
    text = details;
  } else {
    try {
      text = JSON.stringify(details);
    } catch {
      text = String(details);
    }
  }
  const trimmed = text.trim();
  if (trimmed.length > limit) {
    return `${trimmed.substring(0, limit)}...`;
  }
  return trimmed;
};

const ActivityMessage = ({
  message,
  timestamp,
  onSelect,
  isSelected = false,
  variant = "panel",
  previewLength = 100,
  formatDetails = true,
  hintText = "Click to open the captured output.",
}) => {
  const parsed = useMemo(
    () => parseActivityMessage(message, { formatDetails }),
    [message, formatDetails]
  );
  const isClickable = parsed.hasDetails && typeof onSelect === "function";
  const previewText = parsed.hasDetails ? getPreviewText(parsed.details, previewLength) : "";

  const handleClick = () => {
    if (isClickable) {
      onSelect(parsed);
    }
  };

  const handleKeyDown = (event) => {
    if (!isClickable) return;
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect(parsed);
    }
  };

  if (variant === "demo") {
    return (
      <div
        className={`w-full rounded-md border px-3 py-2 text-left transition-colors hover:bg-muted ${
          parsed.hasDetails ? "border-primary/60" : "border-border"
        } ${isClickable ? "cursor-pointer" : ""}`}
        role={isClickable ? "button" : undefined}
        tabIndex={isClickable ? 0 : undefined}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="font-medium">{parsed.activity || message}</span>
          <span className="text-xs text-muted-foreground">
            {timestamp ? new Date(timestamp).toLocaleTimeString() : "—"}
          </span>
        </div>
        {parsed.hasDetails && (
          <p className="mt-1 text-xs text-muted-foreground">{hintText}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`text-sm text-gray-700 dark:text-gray-300 py-1 rounded px-2 transition-colors ${
        parsed.hasDetails ? "border-l-2 border-blue-400 pl-2" : ""
      } ${isSelected ? "bg-yellow-100 dark:bg-yellow-900/20 border-yellow-400" : ""} ${
        isClickable ? "cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-800" : ""
      }`}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {parsed.activity}
          </div>
          {parsed.hasDetails && (
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1 truncate">
              {previewText}
            </div>
          )}
        </div>
        {parsed.hasDetails && (
          <span className="text-xs text-blue-600 dark:text-blue-400 ml-2 flex-shrink-0">
            📋
          </span>
        )}
      </div>
    </div>
  );
};

export default ActivityMessage;
